import React, { useEffect, useState } from 'react';
import {
  ShoppingCart,
  ArrowRight,
  Check,
  Heart,
  Flame,
  Star,
  Sparkles,
  Zap,
} from 'lucide-react';
import Lottie from 'lottie-react';
import {
  type StandbyButtonConfig,
  type IconType,
  getStandbyButtonStyles,
  getCustomStandbyLottie,
} from '../services/standbyButtonDesign';

interface StandbyButtonProps {
  config: StandbyButtonConfig;
  fallbackText: string;
  onClick: () => void;
}

const ICON_MAP: Record<string, React.FC<{ size?: number; color?: string }> | null> = {
  cart: ShoppingCart,
  arrow: ArrowRight,
  check: Check,
  heart: Heart,
  flame: Flame,
  star: Star,
  sparkle: Sparkles,
  bolt: Zap,
  dot: null,   // rendu custom (point lumineux)
  none: null,
};

const LOTTIE_SIZE_PX: Record<'sm' | 'md' | 'lg', number> = {
  sm: 32,
  md: 48,
  lg: 64,
};

/**
 * v5 — Bouton "Cliquez pour commander" de l'écran de veille.
 *
 * - Animations toujours actives
 * - Supporte Lottie custom
 * - Styles complètement personnalisables via la config admin
 */
export const StandbyButton: React.FC<StandbyButtonProps> = ({
  config,
  fallbackText,
  onClick,
}) => {
  const [lottieData, setLottieData] = useState<any | null>(null);

  // Charge la Lottie custom si activée
  useEffect(() => {
    let mounted = true;
    if (config.showLottie && config.hasCustomLottie) {
      getCustomStandbyLottie().then((d) => {
        if (mounted) setLottieData(d);
      });
    } else {
      setLottieData(null);
    }

    const onUpdate = () => {
      if (config.showLottie && config.hasCustomLottie) {
        getCustomStandbyLottie().then((d) => {
          if (mounted) setLottieData(d);
        });
      }
    };
    window.addEventListener('standbyCustomLottieUpdated', onUpdate);
    return () => {
      mounted = false;
      window.removeEventListener('standbyCustomLottieUpdated', onUpdate);
    };
  }, [config.showLottie, config.hasCustomLottie]);

  const displayText = config.customText.trim() || fallbackText;
  const { containerStyle, buttonStyle, className, textStyle } = getStandbyButtonStyles(config);

  // Icône
  let iconEl: React.ReactNode = null;
  if (config.icon === 'dot') {
    // Point lumineux animé (style du design actuel)
    iconEl = (
      <span style={{ position: 'relative', display: 'inline-flex', width: 16, height: 16 }}>
        <span
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background: `linear-gradient(45deg, ${config.glowColor || '#3b82f6'}, #8b5cf6)`,
            opacity: 0.75,
            animation: 'standby-pulse 1.5s infinite',
          }}
        />
        <span
          style={{
            position: 'relative',
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: `linear-gradient(45deg, ${config.glowColor || '#3b82f6'}, #8b5cf6)`,
          }}
        />
      </span>
    );
  } else if (config.icon !== 'none') {
    const IconComp = ICON_MAP[config.icon];
    if (IconComp) {
      iconEl = <IconComp size={Math.max(18, parseInt(buttonStyle.fontSize as string) - 2)} />;
    }
  }

  // Lottie
  const lottiePx = LOTTIE_SIZE_PX[config.lottieSize];
  const lottieEl =
    config.showLottie && lottieData ? (
      <div style={{ width: lottiePx, height: lottiePx, flexShrink: 0 }}>
        <Lottie animationData={lottieData} loop autoplay style={{ width: '100%', height: '100%' }} />
      </div>
    ) : null;

  return (
    <div style={containerStyle}>
      <button
        type="button"
        onClick={onClick}
        className={className}
        style={buttonStyle}
        aria-label={displayText}
      >
        {/* Position gauche : Lottie puis icône */}
        {lottieEl && config.lottiePosition === 'left' && lottieEl}
        {iconEl && config.iconPosition === 'left' && iconEl}

        <span style={textStyle}>{displayText}</span>

        {iconEl && config.iconPosition === 'right' && iconEl}
        {lottieEl && config.lottiePosition === 'right' && lottieEl}
      </button>
    </div>
  );
};

export default StandbyButton;
